/**
 * Resolves the media grant for `/call/:callId`.
 *
 * Fast path: the grant the placing/answering request already minted, handed
 * over in `activeCallStore`. Cold path (reload, second tab, pasted link):
 * `POST /calls/:id/join`, which re-mints one for a participant already in the
 * call. Without the cold path a refresh drops you out of a live call.
 */
import { useQuery } from '@tanstack/react-query';
import type { Call, CallCredentials, CallKind } from '@cyphex/types/calls';
import { useStoreValue } from '@/lib/tinyStore';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { callWindowStartsConnected } from '@/features/desktop/callWindow';
import { callsApi } from '../data/callsApi';
import { activeCallStore, selectHandoff } from '../store/activeCallStore';

export interface CallGrantState {
  readonly credentials: CallCredentials | null;
  readonly kind: CallKind;
  readonly peerName: string;
  readonly withVideo: boolean;
  readonly startsConnected: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

const peerNameOf = (call: Call | undefined, meId: string | null): string => {
  const other = call?.participants.find((party) => party.user && party.user.id !== meId);
  return other?.user?.displayName ?? call?.initiator.displayName ?? 'Call';
};

export function useCallGrant(callId: string): CallGrantState {
  const { user } = useAuth();
  const handoff = useStoreValue(activeCallStore, selectHandoff);
  const matched = handoff !== null && handoff.callId === callId ? handoff : null;

  const query = useQuery({
    queryKey: ['calls', 'grant', callId],
    queryFn: () => callsApi.join(callId),
    enabled: matched === null && callId !== '',
    /* Single-use by nature and already replayed by the store — refetching would
       mint a second identity into the same room. */
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (matched !== null) {
    return {
      credentials: matched.credentials,
      kind: matched.kind,
      peerName: matched.peerName,
      withVideo: matched.withVideo,
      startsConnected: matched.connected,
      isLoading: false,
      isError: false,
    };
  }

  return {
    credentials: query.data?.credentials ?? null,
    kind: query.data?.call.kind ?? 'audio',
    peerName: peerNameOf(query.data?.call, user?.id ?? null),
    withVideo: (query.data?.call.kind ?? 'audio') === 'video',
    /*
     * A rejoin is by definition already answered, so this path never shows a
     * ringing screen — EXCEPT in a desktop call window opened for an outgoing
     * call, which has no handoff to inherit (the grant was minted in the
     * workspace window, in a store this document cannot see) and so lands here
     * too. Without the shell's `ring=1` hint, placing a call on the desktop
     * would open on a silent empty room instead of "Ringing...".
     */
    startsConnected: callWindowStartsConnected(),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
